import moment from 'moment';
import axios from 'axios';
import OpenIDClient from './OpenIDClient'; // { anonymousOpenID, signUpOpenID, authenticatedOpenID }
import ApiClient from './ApiClient';

const UserClientDateFormat = 'YYYY-MM-DD';

/**
 * Reduce a configured `authEndpoint` to the Keycloak **realm base** (no
 * trailing slash), so callers can safely append their own path.
 *
 * `authEndpoint` is not consistent across tenants — it may be the realm base
 * (`.../realms/SDIS06-FRNCE` or `.../realms/SDIS06-FRNCE/`) or the full token
 * endpoint (`.../realms/LEONGO-FRLIO/protocol/openid-connect/token`). We keep
 * only the part before `/protocol/` and strip any trailing slash.
 *
 * @param {string} authEndpoint - the tenant's configured auth endpoint
 * @returns {string} the realm base URL without a trailing slash
 */
export const realmBaseEndpoint = authEndpoint => authEndpoint.split('/protocol/')[0].replace(/\/+$/, '');

/**
 * Build the IDP `.well-known/method` discovery URL (which advertises the
 * enabled auth modes) from the tenant `authEndpoint` + client id.
 *
 * @param {string} authEndpoint - the tenant's configured auth endpoint
 * @param {string} clientId - the OIDC client id
 * @returns {string} the full discovery URL
 */
export const authMethodsDiscoveryUrl = (authEndpoint, clientId) =>
    `${realmBaseEndpoint(authEndpoint)}/.well-known/method?client_id=${clientId}`;

class UserClient extends ApiClient {
    init = apiConf => {
        this.apiConf = apiConf;
    };

    /**
     * Verify a user's password without establishing/replacing the session:
     * a bare ROPC (`grant_type=password`) POST to the token endpoint that
     * resolves on success and rejects on failure. The tokens it returns are
     * discarded — the @vulog/auth Service Worker owns the real session.
     */
    verifyPassword = (username, password) => {
        const { authEndpoint, authenticatedAccess } = this.apiConf;
        const url = `${realmBaseEndpoint(authEndpoint)}/protocol/openid-connect/token`;
        const params = new URLSearchParams();
        params.append('username', username);
        params.append('password', password);
        params.append('client_id', authenticatedAccess.clientId);
        params.append('client_secret', authenticatedAccess.clientSecret);
        params.append('securityOptions', 'SSL_OP_NO_SSLv3');
        params.append('grant_type', 'password');
        return axios
            .post(url, params.toString(), {
                headers: {
                    'X-API-Key': authenticatedAccess.apiKey,
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
            })
            .catch(e => Promise.reject(e.response));
    };

    getUser = () => this.get(OpenIDClient.authenticatedOpenID, `${this.apiConf.baseEndpoint}user`);

    getUserProfile = () => this.get(OpenIDClient.authenticatedOpenID, `${this.apiConf.baseEndpoint}user/profile`);

    /**
     * Discover the tenant's enabled auth methods from the IDP's
     * `.well-known/method` document (drives which login modes are offered).
     * Mirrors @vulog/auth's `discoverAuthMethods`, but kept here because it
     * must send the app `X-API-Key` and the discovery endpoint sits at the
     * realm root — outside the Service Worker's intercept scope.
     */
    discoverAuthMethods = ({ authEndpoint, clientId }) => {
        const url = authMethodsDiscoveryUrl(authEndpoint, clientId);
        return this.get(OpenIDClient.authenticatedOpenID, url, undefined, undefined, {
            headers: {
                'X-API-Key': this.apiConf.authenticatedAccess.apiKey,
            },
        });
    };

    getUserServices = () => this.get(OpenIDClient.authenticatedOpenID, `${this.apiConf.baseEndpoint}user/services`);

    getUserDocuments = () => this.get(OpenIDClient.authenticatedOpenID, `${this.apiConf.baseEndpoint}user/documents`);

    getPaymentDetails = entityId =>
        this.get(OpenIDClient.authenticatedOpenID, `${this.apiConf.baseEndpoint}user/paymentMethodDetails/${entityId}`);

    getCities = () => this.get(OpenIDClient.authenticatedOpenID, `${this.apiConf.baseEndpoint}cities`);

    updateUser = user => this.put(OpenIDClient.authenticatedOpenID, `${this.apiConf.baseEndpoint}user`, user);

    updateProfile = profile =>
        this.put(OpenIDClient.authenticatedOpenID, `${this.apiConf.baseEndpoint}user/profiles/${profile.id}`, profile);

    createUser = ({ login, email, password, locale }) =>
        this.post(
            OpenIDClient.signUpOpenID,
            `${this.apiConf.baseEndpoint}users`,
            { login, email, password, locale },
            resolve => () => resolve(),
            reject => error => {
                if (error.response) {
                    switch (error.response.status) {
                        case 422: // Login or email already exist (parse the body)
                            reject(this.buildReply(error.response.status, 'User already exists!'));
                            break;
                        default:
                            reject(this.buildReply(error.response.status, 'Some error occurred'));
                            break;
                    }
                } else {
                    reject(this.buildReply(error.response.status, 'Some unrecognised error occurred'));
                }
            }
        );

    createDocument = ({
        documentType,
        documentNumber,
        expirationDate,
        issuingSubdivision,
        issuingCountry,
        issuingDate,
        issuingOffice,
        dlClass,
    }) => {
        const apiUrl = `${this.apiConf.baseEndpoint}user/documents`;
        return this.post(OpenIDClient.authenticatedOpenID, apiUrl, {
            documentType,
            documentNumber,
            expirationDate,
            issuingCountry,
            issuingDate,
            issuingOffice,
            dlClass,
            ...(issuingSubdivision && { issuingSubdivision }),
        });
    };

    /**
     * Uploads a document attachment (multipart) to the URL provided by the API.
     *
     * The axios instance declares `Content-Type: application/json` by default, and axios
     * serialises a FormData payload to JSON when that content type is set (the request then
     * carries `{"file":{}}` and the API replies 400 / "File not provided"). Unsetting the
     * header lets the browser build the `multipart/form-data` content type with its boundary.
     *
     * @param {{ url: string, name?: string }} uploadEndpoint Upload target returned by the API.
     * @param {FormData} file FormData holding the `file` entry to upload.
     * @returns {Promise<*>} Resolves with the API response body.
     */
    uploadDocument = (uploadEndpoint, file) =>
        this.post(OpenIDClient.authenticatedOpenID, uploadEndpoint.url, file, this.customResolve, this.customReject, {
            headers: { 'Content-Type': undefined },
        });

    changePassword = password =>
        this.post(
            OpenIDClient.authenticatedOpenID,
            `${this.apiConf.baseEndpoint}changePassword`,
            { password },
            resolve => () => resolve(this.buildReply(200, 'ok'))
        );

    sendLostPasswordEmailWithRecaptcha = ({ email: login, recaptchaToken, recaptchaSiteKey }) => {
        const instance = axios.create({
            baseURL: this.apiConf.authEndpoint.replace('/protocol/openid-connect/token', ''),
            timeout: 30000,
            headers: {
                'Cache-Control': 'no-cache',
                'Content-Type': 'application/json',
            },
            withCredentials: false,
        });

        return instance
            .post(`/forgot-password`, {
                login,
                recaptcha_token: recaptchaToken,
                recaptcha_site_key: recaptchaSiteKey,
            })
            .then(() => this.buildReply(200, 'ok'));
    };

    sendLostPasswordEmail = login =>
        this.post(
            OpenIDClient.anonymousOpenID,
            `${this.apiConf.baseEndpoint}sendLostPasswordEmail`,
            { login },
            resolve => () => resolve(this.buildReply(200, 'ok'))
        );

    resetLostPassword = (token, password) =>
        this.post(
            OpenIDClient.anonymousOpenID,
            `${this.apiConf.baseEndpoint}resetLostPassword`,
            { token, password },
            resolve => () => resolve(this.buildReply(200, 'ok'))
        );

    fetchMyTrips = (
        profileType,
        profileId,
        startDate = moment()
            .subtract(10, 'years')
            .utc()
            .format(UserClientDateFormat),
        endDate = moment()
            .utc()
            .format(UserClientDateFormat)
    ) => {
        let queryParam;
        if (profileId && profileType)
            queryParam = `startDate=${startDate}&endDate=${endDate}&profileType=${profileType}&profileId=${profileId}`;
        else if (profileType) queryParam = `startDate=${startDate}&endDate=${endDate}&profileType=${profileType}`;
        else queryParam = `startDate=${startDate}&endDate=${endDate}`;
        return this.get(
            OpenIDClient.authenticatedOpenID,
            `${this.apiConf.baseEndpoint}invoices/tripDetails?${queryParam}`
        );
    };

    fetchMyProducts = (
        startDate = moment()
            .utc()
            .subtract(10, 'years')
            .format(UserClientDateFormat),
        endDate = moment()
            .utc()
            .format(UserClientDateFormat)
    ) =>
        this.get(
            OpenIDClient.authenticatedOpenID,
            `${this.apiConf.baseEndpoint}invoices/productDetails?startDate=${startDate}&endDate=${endDate}`
        );

    fetchMyOldInvoices = () =>
        this.get(OpenIDClient.authenticatedOpenID, `${this.apiConf.baseEndpoint}listMonthsWithOldInvoices`);

    fetchMyPromoCodes = entityId =>
        this.get(OpenIDClient.authenticatedOpenID, `${this.apiConf.baseEndpoint}wallet/entity/${entityId}`);

    agreeToCityTerms = cityId =>
        this.post(OpenIDClient.authenticatedOpenID, `${this.apiConf.baseEndpoint}termsOfUse`, cityId);

    getSystemCreditsPackages = () =>
        this.get(OpenIDClient.authenticatedOpenID, `${this.apiConf.baseEndpoint}systemCreditsPackages`);

    onPackagePurchase = id =>
        this.post(
            OpenIDClient.authenticatedOpenID,
            `${this.apiConf.baseEndpoint}user/profile/purchaseSystemCreditsPackages/${id}`
        );

    getPromoCodeFleetsAvailability = promoCode =>
        this.get(
            OpenIDClient.authenticatedOpenID,
            `${this.apiConf.baseEndpoint}user/promocodes/reference/${promoCode}/cities`
        );

    redeemPromoCode = ({ promoCode, selectedCity }) =>
        this.post(
            OpenIDClient.authenticatedOpenID,
            `${this.apiConf.baseEndpoint}user/promocodes/reference/${promoCode}/redeem`,
            {
                cityId: selectedCity || null,
            }
        );

    urlInvoice = (token, invoiceId) =>
        `${this.apiConf.baseEndpoint}invoices/${invoiceId}/pdf?access_token=${token}&x-api-key=${this.apiConf.authenticatedAccess.apiKey}`;

    getOldInvoice = date =>
        this.get(OpenIDClient.authenticatedOpenID, `${this.apiConf.baseEndpoint}oldInvoicesUrl?date=${date}`);

    getPricing = () => this.get(OpenIDClient.authenticatedOpenID, `${this.apiConf.baseEndpoint}pricing`);

    getPricingById = pricingId =>
        this.get(OpenIDClient.authenticatedOpenID, `${this.apiConf.baseEndpoint}pricing/${pricingId}`);

    getUserSubscriptions = profileId =>
        this.get(
            OpenIDClient.authenticatedOpenID,
            `${this.apiConf.baseEndpoint}users/subscriptions?profileId=${profileId}`
        );

    getMobilityPlans = () => this.get(OpenIDClient.authenticatedOpenID, `${this.apiConf.baseEndpoint}plans`);

    fetchPlanFees = () => this.get(OpenIDClient.authenticatedOpenID, `${this.apiConf.baseEndpoint}fees`);

    subscribeToAPlan = payload =>
        this.post(OpenIDClient.authenticatedOpenID, `${this.apiConf.baseEndpoint}users/subscriptions`, payload);

    unsubscribeFromAPlan = ({ subscriptionId, profileId }) =>
        this.put(
            OpenIDClient.authenticatedOpenID,
            `${this.apiConf.baseEndpoint}users/subscriptions/${subscriptionId}`,
            { profileId }
        );

    onPayFailedInvoices = () =>
        this.post(OpenIDClient.authenticatedOpenID, `${this.apiConf.baseEndpoint}user/invoices/payAll`);

    onSetupIntent = ({ entityId, ...updatedPayload }) =>
        this.post(
            OpenIDClient.authenticatedOpenID,
            `${this.apiConf.baseEndpoint}user/entity/${entityId}/setup_intents`,
            { ...updatedPayload }
        );

    synchronizeSetupIntent = (entityId, setupIntentId, redirectResult) =>
        this.post(
            OpenIDClient.authenticatedOpenID,
            `${this.apiConf.baseEndpoint}user/entity/${entityId}/setup_intents/${setupIntentId}/synchronize`,
            { ...(redirectResult && { rawData: { redirectResult } }) }
        );

    getPublishableKey = () =>
        this.get(OpenIDClient.authenticatedOpenID, `${this.apiConf.baseEndpoint}user/mop/stripe/key`);

    getPublishableKeyForIntent = () =>
        this.get(OpenIDClient.authenticatedOpenID, `${this.apiConf.baseEndpoint}user/mop/key`);

    getInvoicesReplayList = () =>
        this.get(OpenIDClient.authenticatedOpenID, `${this.apiConf.baseEndpoint}paymentOptions`);

    onPayInvoices = payload =>
        this.post(OpenIDClient.authenticatedOpenID, `${this.apiConf.baseEndpoint}user/invoices/replays`, payload);

    getPasswordStrength = () => {
        return this.get(OpenIDClient.authenticatedOpenID, `${this.apiConf.baseEndpoint}user/passwordStrength`);
    };

    requestAccountDeletionEndpoint = () =>
        this.post(OpenIDClient.authenticatedOpenID, `${this.apiConf.baseEndpoint}user/privacy/inactiveaccount`);

    getBillingGroup = billingGroupId =>
        this.get(OpenIDClient.authenticatedOpenID, `${this.apiConf.baseEndpoint}billingGroup/${billingGroupId}`);

    getOngoingTrips = () =>
        this.get(OpenIDClient.authenticatedOpenID, `${this.apiConf.baseEndpoint}user/currentJourneys`);
}

export default new UserClient();
